import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, Empty, Row, Select, Spin, Button } from 'antd';
import moment from 'moment-timezone';

import { useByDateFilter } from '../../../../shared/charts/hooks/ByDateFilter';
import DatePickerField from '../../../../shared/charts/components/DatePickerField';
import DualAxisAreaChart from '../../../../shared/designSystem/components/DualAxisAreaChart/DualAxisAreaChart';
import { MenuItemTextColor } from '../../../../shared/globalStyling/styledText';
import { colStyle } from '../../../../shared/styles/chart';
import { fillDateSeries } from '../../../Campaigns/campaigndetails/utils';

const { Option } = Select;

// =============================================================================
// EditorialSummaryChart — editorial trend chart on the v2 Recharts
// DualAxisAreaChart with an optional GA-style comparison overlay.
//
// Mirrors the shared BizCharts DualAxisChart shell (two metric selectors + the
// primary DatePickerField + "Reset to Default") so the date picker keeps
// driving the page's date range, but renders the Recharts chart so a dashed
// "previous period" line can overlay the current series. When comparisonByDate
// is provided — rows carrying `prev_<key>` + `previousDate` from
// utils/comparison.buildComparisonSeries — the selected LEFT metric gets the
// dashed comparison line (index-aligned, GA day-1-vs-day-1 semantics).
// =============================================================================
const EditorialSummaryChart = ({
  id,
  byDate = [],
  xAxis = 'xAxis',
  options = [],
  defaultLeftYValue,
  defaultRightYValue,
  defaultSummaryDates,
  onDatePickerChange = () => {},
  loading = false,
  has6MonDisables = false,
  allTimeDateRange,
  onClearSessionData = () => {},
  hasSavedSession = false,
  comparisonByDate = [],
}) => {
  const {
    rangeKey,
    setRangeKey,
    filteredData,
    startDateEndDate,
    customDateRange,
    setCustomDateRange,
    emptyData,
  } = useByDateFilter(byDate, xAxis, id, defaultSummaryDates, false);

  const resolvedDefaultDates = useMemo(() => {
    if (defaultSummaryDates) return defaultSummaryDates;
    if (!byDate.length) return undefined;
    const sorted = [...byDate].sort(
      (a, b) => moment(a[xAxis]).utc(true).unix() - moment(b[xAxis]).utc(true).unix()
    );
    return [
      moment(sorted[0][xAxis]).utc(true),
      moment(sorted[sorted.length - 1][xAxis]).utc(true),
    ];
  }, [byDate, xAxis, defaultSummaryDates]);

  useEffect(() => {
    if (byDate.length === 0 && resolvedDefaultDates) {
      setCustomDateRange(resolvedDefaultDates);
      setRangeKey('All Time');
    }
  }, [byDate, resolvedDefaultDates, setCustomDateRange, setRangeKey]);

  const [leftKey, setLeftKey] = useState(
    defaultLeftYValue ? defaultLeftYValue.key : options[0] && options[0].key
  );
  const [rightKey, setRightKey] = useState(
    defaultRightYValue ? defaultRightYValue.key : options[1] && options[1].key
  );
  const [dateChanged, setDateChanged] = useState(false);

  // Only offer metrics actually present on the data.
  const byDateKeys = Object.keys(byDate && byDate.length && byDate[0] ? byDate[0] : {});
  let keys = options.filter((option) => byDateKeys.includes(option.key));
  let isDropdownDisabled = false;
  if (keys.length === 0) {
    keys = options;
    isDropdownDisabled = true;
  }
  isDropdownDisabled = emptyData ? true : isDropdownDisabled;
  const isPickerDisabled = emptyData;

  useEffect(() => {
    if (dateChanged) {
      onDatePickerChange({
        data: filteredData,
        dates: [
          moment(customDateRange[0], 'YYYY-MM-DD'),
          moment(customDateRange[1], 'YYYY-MM-DD'),
        ],
        rangeKey,
      });
      setDateChanged(false);
    }
  }, [dateChanged, filteredData, onDatePickerChange, customDateRange, rangeKey]);

  const displayFor = (key) => {
    const found = options.find((option) => option.key === key);
    return found ? found.display : key;
  };

  // Metric keys the zero-fill has to synthesize for a gap day. Numeric 0 (not
  // undefined) so the area drops to the baseline instead of breaking the line.
  const metricKeys = useMemo(() => options.map((option) => option.key), [options]);

  const comparisonActive = comparisonByDate && comparisonByDate.length > 0;
  // With comparison on, render the merged series (current metrics + prev_<key>);
  // otherwise the normal filtered current series. Normalize the x-axis + tooltip
  // dates to a plain calendar day (editorial byDate.date is an ISO datetime;
  // `.utc(true)` matches the rest of the page) so the chart never renders a raw
  // timestamp / timezone.
  const chartData = useMemo(() => {
    const rows = comparisonActive ? comparisonByDate : filteredData;
    if (!Array.isArray(rows)) return rows;
    // The Recharts chart reads the day from a fixed `xAxis` key, so map the
    // source date field (which may be `date` on some tabs) onto `xAxis` and
    // format it as a plain calendar day (no ISO timestamp / timezone).
    const normalized = rows.map((row) => {
      const raw = row[xAxis];
      return {
        ...row,
        xAxis: raw != null ? moment(raw).utc(true).format('YYYY-MM-DD') : raw,
        ...(row.previousDate
          ? { previousDate: moment(row.previousDate).utc(true).format('YYYY-MM-DD') }
          : {}),
      };
    });

    // Comparison rows already come out of buildComparisonSeries gap-filled and
    // in ascending day order — leave the index alignment it built untouched.
    if (comparisonActive) return normalized;

    // Zero-fill the current series so the X axis is a CONTINUOUS run of days.
    // The report's byDate rows only carry days that actually had activity, and
    // the Recharts X axis is categorical: a missing day gets no slot at all, so
    // the axis rendered as e.g. 8/1, 8/2, 8/5, 8/9 evenly spaced — days with no
    // data silently vanished and the remaining ones read as consecutive. Some
    // sources (EditorialDetailsController.filterByDropdown) also build byDates
    // in transaction-arrival order rather than date order, so the series could
    // even run backwards. fillDateSeries walks the day range start→end, keeping
    // real rows and synthesizing { xAxis, ...metrics: 0 } for the gaps, which
    // fixes both the holes and the ordering.
    const days = normalized.map((row) => row.xAxis).filter(Boolean).sort();
    if (days.length < 2) return normalized;
    // fillDateSeries indexes rows by day, so two rows landing on the same day
    // would collapse into one. Every source here aggregates per day, but if one
    // ever doesn't, leave the series alone rather than silently drop a row.
    const hasDuplicateDays = days.some((d, i) => i > 0 && d === days[i - 1]);
    if (hasDuplicateDays) return normalized;
    return fillDateSeries(normalized, days[0], days[days.length - 1], {
      xAxisKey: 'xAxis',
      zeroFields: metricKeys,
    });
  }, [comparisonActive, comparisonByDate, filteredData, xAxis, metricKeys]);
  // Dual-axis chart -> compare BOTH the left and right metrics. buildComparisonSeries
  // carries prev_<key> for every metric, so each axis gets its own dashed line.
  const comparisonSeries = comparisonActive
    ? {
        left: { dataKey: `prev_${leftKey}`, name: `Previous ${displayFor(leftKey)}` },
        right: rightKey
          ? { dataKey: `prev_${rightKey}`, name: `Previous ${displayFor(rightKey)}` }
          : undefined,
      }
    : undefined;

  return (
    <Card
      style={colStyle.container}
      bodyStyle={{ padding: 18 }}
      title={
        <Row justify="space-between" align="middle">
          <Col>Summary</Col>
          {hasSavedSession ? <Button onClick={onClearSessionData}>Reset to Default</Button> : null}
        </Row>
      }
    >
      <Card bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Row style={{ marginBottom: '2%' }} justify="space-between" gutter={[8, 8]}>
            <Col xs={24} md={5} lg={4} xl={4}>
              <Select
                onChange={(value) => setLeftKey(value)}
                style={{ width: '100%' }}
                value={leftKey}
                disabled={isDropdownDisabled}
              >
                {keys
                  .filter((option) => option.key !== rightKey)
                  .map((option) => (
                    <Option key={option.key} value={option.key}>
                      <MenuItemTextColor text={option.display} color={'#7dafff'} />
                    </Option>
                  ))}
              </Select>
            </Col>
            <Col xs={24} md={14} lg={16} xl={16}>
              <Row justify="center">
                <DatePickerField
                  setCustomDateRange={setCustomDateRange}
                  setDateChanged={setDateChanged}
                  data={filteredData}
                  defaultPickerValue={startDateEndDate}
                  defaultValue={startDateEndDate}
                  value={startDateEndDate}
                  dropDownValue={rangeKey}
                  setDropDownValue={setRangeKey}
                  has6MonDisables={has6MonDisables}
                  showDropdown={true}
                  allTimeDateRange={allTimeDateRange ? allTimeDateRange : resolvedDefaultDates}
                  disabled={isPickerDisabled}
                  timezone="UTC"
                />
              </Row>
            </Col>
            <Col xs={24} md={5} lg={4} xl={4}>
              <Select
                onChange={(value) => setRightKey(value)}
                style={{ width: '100%' }}
                value={rightKey}
                disabled={isDropdownDisabled}
              >
                {keys
                  .filter((option) => option.key !== leftKey)
                  .map((option) => (
                    <Option key={option.key} value={option.key}>
                      <MenuItemTextColor text={option.display} color={'#92d5a7'} />
                    </Option>
                  ))}
              </Select>
            </Col>
          </Row>
          {chartData && chartData.length > 0 ? (
            <DualAxisAreaChart
              dualChartData={chartData}
              leftY={displayFor(leftKey)}
              rightY={displayFor(rightKey)}
              leftKey={leftKey}
              rightKey={rightKey}
              comparisonSeries={comparisonSeries}
            />
          ) : (
            <Empty description="No Data To Display" />
          )}
        </Spin>
      </Card>
    </Card>
  );
};

export default EditorialSummaryChart;
